import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabaseClient';
import { STORAGE_KEYS } from '../config/constants';

export const TermsGatePage: React.FC = () => {
  const { darkMode } = useTheme();
  const { user, setAppState } = useAuth();
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canAccept = termsChecked && privacyChecked;

  const handleAccept = async () => {
    if (!canAccept || !user) return;
    setIsSubmitting(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ terms_accepted_at: new Date().toISOString() })
        .eq('id', user.id);

      if (updateError) throw updateError;

      localStorage.setItem(STORAGE_KEYS.TERMS_ACCEPTED, 'true');
      // Re-trigger auth flow — terms check will now pass
      setAppState('loading');
      window.location.reload();
    } catch (err) {
      console.error('Failed to accept terms:', err);
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const checkboxClass = `w-4 h-4 rounded border-2 flex-shrink-0 cursor-pointer transition-colors ${
    darkMode ? 'border-white/30' : 'border-slate-300'
  }`;

  return (
    <div className={`min-h-[100dvh] flex items-center justify-center px-4 py-8 ${
      darkMode
        ? 'bg-slate-950'
        : 'bg-[#f8f9ff]'
    }`}>
      <div className={`w-full max-w-md mx-4 p-8 rounded-3xl backdrop-blur-xl ${
        darkMode ? 'bg-slate-800/70 border border-slate-700/50' : 'bg-white/70 border border-white/40'
      } shadow-2xl`}>
        <div className="text-center mb-6">
          <div className="rounded-xl p-1.5 mx-auto mb-3 w-fit bg-gradient-to-b from-white/90 to-white border border-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.12),inset_0_1px_2px_rgba(255,255,255,0.9),inset_0_-1px_3px_rgba(0,0,0,0.06)] backdrop-blur-xl">
            <img src="/logo.png" alt="TallyUp Finance" className="h-8 w-8" />
          </div>
          <h1 className={`text-2xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Review and Accept
          </h1>
          <p className={`text-sm mt-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Please review and accept our terms before continuing.
          </p>
        </div>

        <div className={`mb-5 px-4 py-3 rounded-xl ${
          darkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'
        }`}>
          <div className="flex items-start gap-2">
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              className={`flex-shrink-0 mt-0.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <p className={`text-xs leading-relaxed ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
              <strong>Your data stays yours.</strong> TallyUp only creates and reads from a single Google Sheet in your Drive — we never see, store, or share your information. That will never change.
            </p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={termsChecked}
              onChange={e => setTermsChecked(e.target.checked)}
              className={checkboxClass}
            />
            <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              I have read and agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className={`underline font-medium ${darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
              >
                Terms of Service
              </a>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={privacyChecked}
              onChange={e => setPrivacyChecked(e.target.checked)}
              className={checkboxClass}
            />
            <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              I have read and agree to the{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className={`underline font-medium ${darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}
              >
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {error && (
          <p className={`text-sm text-center mb-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
            {error}
          </p>
        )}

        <button
          onClick={handleAccept}
          disabled={!canAccept || isSubmitting}
          className={`w-full px-6 py-3 rounded-xl font-semibold transition-all ${
            !canAccept || isSubmitting
              ? 'bg-violet-600/50 text-white/50 cursor-not-allowed'
              : 'bg-violet-600 hover:bg-violet-700 text-white'
          }`}
        >
          {isSubmitting ? 'Accepting...' : 'Accept and Continue'}
        </button>

        <p className={`text-[10px] text-center mt-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          TallyUp Finance is a tracking tool only and does not provide financial advice.
        </p>
      </div>
    </div>
  );
};
